import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import SEO from "@/components/SEO";
import Mandala from "@/components/Mandala";
import { Loader2, CalendarDays, Clock, Users, ShieldCheck, ChevronRight } from "lucide-react";
import { Waivers } from "@/components/Waivers";

/**
 * Public Reiki booking flow:
 *  1) Pick a tier
 *  2) Pick a date + available time slot
 *  3) Contact + attendee details
 *  4) Redirect to Stripe (mode="setup" for hold, or mode="payment" for group deposit)
 * On success, Stripe redirects back to /reiki/success?session_id=…
 */
const STEPS = ["tier", "when", "details"];

function isoDate(d) {
  // Local-date YYYY-MM-DD (NOT UTC). Using toISOString() here previously
  // caused a subtle off-by-one for evening bookers in negative-offset
  // timezones — the button label read "Sat, Sep 26" while the value
  // (UTC-derived) was "2026-09-27", so admin blocks landed on the wrong
  // day.
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const dd = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${dd}`;
}

function fmtTime(iso) {
  const dt = new Date(iso);
  return dt.toLocaleTimeString([], { hour: "numeric", minute: "2-digit" });
}

export default function ReikiBooking() {
  const { user } = useAuth();
  const [step, setStep] = useState("tier");
  const [tiers, setTiers] = useState([]);
  const [tier, setTier] = useState(null);
  const [selectedDate, setSelectedDate] = useState(() => {
    const d = new Date();
    d.setDate(d.getDate() + 1);
    return isoDate(d);
  });
  const [slots, setSlots] = useState([]);
  const [loadingSlots, setLoadingSlots] = useState(false);
  const [slot, setSlot] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const [form, setForm] = useState({
    lead_name: "",
    lead_email: "",
    lead_phone: "",
    intention: "",
    physical_limitations: "",
    attendees: [],
    subscribe_to_newsletter: true,
    liability_waiver_agreed: false,
    photo_release_agreed: false,
  });

  // Prefill on login
  useEffect(() => {
    if (user) {
      setForm((f) => ({
        ...f,
        lead_name: user.name || f.lead_name,
        lead_email: user.email || f.lead_email,
      }));
    }
  }, [user]);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/reiki/tiers");
        setTiers(data.tiers || []);
      } catch (e) {
        toast.error("Could not load session options");
      }
    })();
  }, []);

  useEffect(() => {
    if (!tier || !selectedDate) return;
    (async () => {
      setLoadingSlots(true);
      setSlot("");
      try {
        const { data } = await api.get("/reiki/availability", {
          params: { tier: tier.slug, day: selectedDate },
        });
        setSlots(data.slots || []);
      } catch (e) {
        setSlots([]);
      } finally {
        setLoadingSlots(false);
      }
    })();
  }, [tier, selectedDate]);

  // Adjust attendees array to match the tier's min count when the tier changes
  useEffect(() => {
    if (!tier) return;
    const extras = Math.max(0, tier.min_attendees - 1); // total - lead
    setForm((f) => {
      const cur = f.attendees || [];
      const trimmed = cur.slice(0, tier.max_attendees - 1);
      while (trimmed.length < extras) trimmed.push({ name: "", email: "" });
      return { ...f, attendees: trimmed };
    });
  }, [tier]);

  const dateOptions = useMemo(() => {
    const out = [];
    const start = new Date();
    // Cap the visible window at the end of November — Holly's tent is
    // taken down in early December so no bookings past Nov 30. If the
    // current year's Nov 30 is already past, roll to next year.
    const end = new Date(start.getFullYear(), 10, 30); // month index 10 = Nov
    if (end < start) end.setFullYear(end.getFullYear() + 1);
    const cursor = new Date(start);
    // Safety cap so we never render an absurdly long list.
    let guard = 0;
    while (cursor <= end && guard < 200) {
      out.push({
        value: isoDate(cursor),
        label: cursor.toLocaleDateString([], { weekday: "short", month: "short", day: "numeric" }),
      });
      cursor.setDate(cursor.getDate() + 1);
      guard += 1;
    }
    return out;
  }, []);

  // Which of the visible dates actually have ≥1 bookable slot. Anything
  // NOT in this set gets rendered as a greyed-out "Unavailable" pill so
  // guests don't waste a click on a fully-blocked day (vacation, tent
  // down, no recurring window, etc.).
  const [availableDays, setAvailableDays] = useState(null); // null = loading
  useEffect(() => {
    if (!tier || dateOptions.length === 0) return;
    const first = dateOptions[0].value;
    const last = dateOptions[dateOptions.length - 1].value;
    let cancelled = false;
    setAvailableDays(null);
    api.get(`/reiki/available-days?tier=${tier.slug}&start=${first}&end=${last}`)
      .then(({ data }) => {
        if (!cancelled) setAvailableDays(new Set(data.days || []));
      })
      .catch(() => { if (!cancelled) setAvailableDays(new Set()); });
    return () => { cancelled = true; };
  }, [tier, dateOptions]);

  const totalPrice = useMemo(() => {
    if (!tier) return 0;
    if (tier.slug === "semi-private") return 185;
    if (tier.slug.startsWith("group-")) {
      const n = 1 + form.attendees.length;
      return n * tier.per_person_usd;
    }
    return tier.price_usd;
  }, [tier, form.attendees.length]);

  const submit = async () => {
    if (!tier || !slot) {
      toast.error("Please pick a date and time");
      return;
    }
    if (!form.lead_name || !form.lead_email || !form.lead_phone) {
      toast.error("Please fill in your contact info");
      return;
    }
    const need = tier.min_attendees - 1;
    if (form.attendees.length < need) {
      toast.error(`This session needs ${tier.min_attendees} attendees total`);
      return;
    }
    if (!form.liability_waiver_agreed || !form.photo_release_agreed) {
      toast.error("Please acknowledge both the Waiver & Release of Liability and the Photo Release.");
      document.querySelector('[data-field="liability_waiver_agreed"]')?.scrollIntoView({ behavior: "smooth", block: "center" });
      return;
    }
    setSubmitting(true);
    try {
      const { data } = await api.post("/reiki/bookings", {
        tier_slug: tier.slug,
        start_iso: slot,
        origin_url: window.location.origin,
        ...form,
      });
      window.location.href = data.checkout_url;
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not complete booking");
      setSubmitting(false);
    }
  };

  return (
    <>
      <SEO
        title="Reset · Realign · Come Home to Yourself — Book a Reiki Session"
        description="Book a private, semi-private, group, or long distance reiki + sound healing session with Holly Johnson. 75-90 minute sessions in Sugar Grove, IL and worldwide."
      />

      {/* Hero */}
      <section className="pt-32 pb-14 bg-gradient-to-b from-aviana-cream/40 to-transparent" data-testid="reiki-hero">
        <div className="container-aviana max-w-4xl text-center">
          <Mandala size={64} variant="full" className="text-aviana-gold mx-auto mb-6" />
          <p className="overline mb-3">Reiki + Sound Healing</p>
          <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl text-aviana-ink leading-[1.02]">
            Reset <span className="text-aviana-gold">·</span> Realign <span className="text-aviana-gold">·</span> <em className="italic gold-text">Come Home to Yourself</em>
          </h1>
          <p className="mt-8 max-w-2xl mx-auto text-aviana-muted leading-[1.85] font-serif italic text-lg">
            Choose the container that fits you — a private hour of quiet, a shared session with someone you love,
            or a small circle. A card is held on file to protect your reservation; you can pay the session cost
            via Stripe, Venmo, or Zelle after your card is on file.
          </p>
        </div>
      </section>

      {/* Progress rail */}
      <div className="container-aviana max-w-4xl mt-6 mb-10 hidden md:flex items-center justify-center gap-4 text-sm">
        {STEPS.map((s, i) => (
          <div key={s} className={`flex items-center gap-2 ${step === s ? "text-aviana-ink" : "text-aviana-muted/70"}`}>
            <span
              className={`w-6 h-6 rounded-full grid place-items-center text-xs
                ${step === s ? "bg-aviana-gold text-white" : "bg-aviana-ink/10 text-aviana-muted"}`}
            >
              {i + 1}
            </span>
            <span className="capitalize">{s === "when" ? "Date & Time" : s === "tier" ? "Session type" : "Your details"}</span>
            {i < STEPS.length - 1 && <ChevronRight size={14} />}
          </div>
        ))}
      </div>

      <div className="container-aviana max-w-4xl pb-24">
        {step === "tier" && (
          <div className="grid gap-5" data-testid="reiki-tier-step">
            {tiers.map((t) => (
              <button
                key={t.slug}
                onClick={() => {
                  setTier(t);
                  setStep("when");
                }}
                className="text-left group border border-aviana-ink/10 hover:border-aviana-gold/60 rounded-2xl p-6 md:p-8 bg-white/60 hover:bg-white transition-all shadow-sm hover:shadow-md"
                data-testid={`reiki-tier-${t.slug}`}
              >
                <div className="flex flex-wrap items-baseline justify-between gap-4">
                  <h3 className="font-display text-2xl md:text-3xl text-aviana-ink">{t.name}</h3>
                  <div className="text-right">
                    <div className="font-figure text-2xl text-aviana-plum">
                      {t.per_person_usd
                        ? `$${t.per_person_usd}/person`
                        : `$${t.price_usd}`}
                    </div>
                    <div className="text-xs uppercase tracking-[0.15em] text-aviana-muted">
                      {t.duration_min} min
                    </div>
                  </div>
                </div>
                <p className="mt-4 text-aviana-muted leading-relaxed text-[15px]">{t.blurb}</p>
                <div className="mt-5 flex flex-wrap gap-4 text-xs text-aviana-muted">
                  <span className="inline-flex items-center gap-1.5">
                    <Users size={14} className="text-aviana-gold" />
                    {t.min_attendees === t.max_attendees ? `${t.min_attendees} attendee(s)` : `${t.min_attendees}-${t.max_attendees} attendees`}
                  </span>
                  <span className="inline-flex items-center gap-1.5">
                    <ShieldCheck size={14} className="text-aviana-gold" />
                    {t.cancellation_hours}h cancellation policy
                  </span>
                  {t.requires_deposit && (
                    <span className="inline-flex items-center gap-1.5 text-aviana-plum">
                      $100 deposit required
                    </span>
                  )}
                </div>
              </button>
            ))}
          </div>
        )}

        {step === "when" && tier && (
          <div data-testid="reiki-when-step">
            <div className="flex items-center justify-between mb-6">
              <button onClick={() => setStep("tier")} className="text-sm text-aviana-muted hover:text-aviana-ink">
                ← Change session type
              </button>
              <div className="text-right">
                <div className="font-display text-xl text-aviana-ink">{tier.name}</div>
                <div className="text-xs text-aviana-muted">{tier.duration_min} min</div>
              </div>
            </div>

            <div className="mb-6">
              <label className="block text-xs uppercase tracking-[0.18em] text-aviana-muted mb-3">
                <CalendarDays size={14} className="inline mr-2 -mt-1" />
                Choose a date
              </label>
              <div className="flex flex-wrap gap-2" data-testid="reiki-date-list">
                {dateOptions.map((d) => {
                  const isSelected = selectedDate === d.value;
                  // availableDays === null → still loading, don't grey anything yet
                  const isUnavailable = availableDays !== null && !availableDays.has(d.value);
                  return (
                    <button
                      key={d.value}
                      onClick={() => { if (!isUnavailable) setSelectedDate(d.value); }}
                      disabled={isUnavailable && !isSelected}
                      title={isUnavailable ? "Unavailable" : undefined}
                      className={`px-3.5 py-2 rounded-full text-sm border transition-all
                        ${isSelected
                          ? "bg-aviana-plum text-white border-aviana-plum"
                          : isUnavailable
                          ? "border-aviana-ink/10 text-aviana-ink/35 bg-aviana-ink/[0.03] line-through cursor-not-allowed"
                          : "border-aviana-ink/15 text-aviana-ink hover:border-aviana-gold"}`}
                      data-testid={`reiki-date-${d.value}${isUnavailable ? "-unavailable" : ""}`}
                    >
                      {d.label}
                    </button>
                  );
                })}
              </div>
              {availableDays !== null && dateOptions.some((d) => !availableDays.has(d.value)) && (
                <p className="mt-2.5 text-[11px] text-aviana-muted italic">
                  Greyed dates are unavailable (blocked or fully booked).
                </p>
              )}
            </div>

            <div>
              <label className="block text-xs uppercase tracking-[0.18em] text-aviana-muted mb-3">
                <Clock size={14} className="inline mr-2 -mt-1" />
                Choose a time
              </label>
              {loadingSlots ? (
                <div className="py-8 text-center text-aviana-muted">
                  <Loader2 size={20} className="inline animate-spin mr-2" />
                  Loading availability…
                </div>
              ) : slots.length === 0 ? (
                <div className="py-8 text-center text-aviana-muted italic">
                  No availability this day — try another date.
                </div>
              ) : (
                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-2" data-testid="reiki-slot-list">
                  {slots.map((s) => (
                    <button
                      key={s}
                      onClick={() => {
                        setSlot(s);
                        setStep("details");
                      }}
                      className="px-3 py-2.5 rounded-md border border-aviana-ink/15 text-sm text-aviana-ink hover:border-aviana-gold hover:bg-aviana-cream/40 transition-all"
                      data-testid={`reiki-slot-${s}`}
                    >
                      {fmtTime(s)}
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}

        {step === "details" && tier && slot && (
          <div data-testid="reiki-details-step">
            <div className="flex items-center justify-between mb-6">
              <button onClick={() => setStep("when")} className="text-sm text-aviana-muted hover:text-aviana-ink">
                ← Change date/time
              </button>
              <div className="text-right">
                <div className="font-display text-lg text-aviana-ink">{tier.name}</div>
                <div className="text-xs text-aviana-muted">
                  {new Date(slot).toLocaleString([], { weekday: "long", month: "short", day: "numeric", hour: "numeric", minute: "2-digit" })}
                </div>
              </div>
            </div>

            <div className="grid md:grid-cols-2 gap-6">
              <Input label="Your name" value={form.lead_name} onChange={(v) => setForm({ ...form, lead_name: v })} testid="reiki-name" />
              <Input label="Email" type="email" value={form.lead_email} onChange={(v) => setForm({ ...form, lead_email: v })} testid="reiki-email" />
              <Input label="Phone" value={form.lead_phone} onChange={(v) => setForm({ ...form, lead_phone: v })} testid="reiki-phone" />
              <div />

              {tier.max_attendees > 1 && (
                <div className="md:col-span-2">
                  <label className="block text-xs uppercase tracking-[0.18em] text-aviana-muted mb-3">
                    Additional attendees ({tier.min_attendees - 1} required, up to {tier.max_attendees - 1})
                  </label>
                  <div className="space-y-3">
                    {form.attendees.map((a, i) => (
                      <div key={i} className="grid sm:grid-cols-2 gap-3">
                        <input
                          placeholder="Attendee name"
                          className={inputCls}
                          value={a.name}
                          onChange={(e) => {
                            const attendees = [...form.attendees];
                            attendees[i].name = e.target.value;
                            setForm({ ...form, attendees });
                          }}
                          data-testid={`reiki-attendee-${i}-name`}
                        />
                        <input
                          placeholder="Attendee email (optional)"
                          className={inputCls}
                          value={a.email}
                          onChange={(e) => {
                            const attendees = [...form.attendees];
                            attendees[i].email = e.target.value;
                            setForm({ ...form, attendees });
                          }}
                        />
                      </div>
                    ))}
                    {form.attendees.length < tier.max_attendees - 1 && (
                      <button
                        onClick={() =>
                          setForm({ ...form, attendees: [...form.attendees, { name: "", email: "" }] })
                        }
                        className="text-sm text-aviana-plum hover:underline"
                        data-testid="reiki-add-attendee"
                      >
                        + Add another attendee
                      </button>
                    )}
                  </div>
                </div>
              )}

              <div className="md:col-span-2">
                <label className="block text-xs uppercase tracking-[0.18em] text-aviana-muted mb-2">
                  Intention or focus for this session (optional)
                </label>
                <textarea
                  className={`${inputCls} min-h-[80px]`}
                  value={form.intention}
                  onChange={(e) => setForm({ ...form, intention: e.target.value })}
                  data-testid="reiki-intention"
                />
              </div>

              <div className="md:col-span-2">
                <label className="block text-xs uppercase tracking-[0.18em] text-aviana-muted mb-2">
                  Physical limitations or notes for Holly (optional)
                </label>
                <textarea
                  className={`${inputCls} min-h-[70px]`}
                  value={form.physical_limitations}
                  onChange={(e) => setForm({ ...form, physical_limitations: e.target.value })}
                />
              </div>

              <div className="md:col-span-2 pt-2">
                <Waivers
                  liabilityAgreed={form.liability_waiver_agreed}
                  onLiabilityChange={(v) => setForm({ ...form, liability_waiver_agreed: v })}
                  photoAgreed={form.photo_release_agreed}
                  onPhotoChange={(v) => setForm({ ...form, photo_release_agreed: v })}
                  testidPrefix="reiki-waiver"
                  subtitle="Required for every Aviana reiki, yoga, and aerial yoga registration."
                />
              </div>

              <label className="md:col-span-2 flex items-start gap-3 text-sm text-aviana-muted">
                <input
                  type="checkbox"
                  className="mt-1"
                  checked={form.subscribe_to_newsletter}
                  onChange={(e) => setForm({ ...form, subscribe_to_newsletter: e.target.checked })}
                />
                <span>Send me Holly's monthly reflections (unsubscribe anytime).</span>
              </label>
            </div>

            {/* Summary + CTA */}
            <div className="mt-10 border-t border-aviana-ink/10 pt-8">
              <div className="flex flex-wrap gap-6 justify-between items-end">
                <div className="text-sm text-aviana-muted max-w-lg leading-relaxed">
                  <p>
                    <ShieldCheck size={14} className="inline -mt-1 mr-1.5 text-aviana-gold" />
                    {tier.requires_deposit ? (
                      <>A <strong>$100 deposit</strong> is charged now to secure your booking. Your card is also saved for the remaining balance and 48-hour cancellation policy.</>
                    ) : (
                      <>Your card is held on file (not charged today). You'll be charged the full session fee only if you cancel within {tier.cancellation_hours} hours or don't show up.</>
                    )}
                  </p>
                </div>
                <div className="text-right">
                  <div className="text-xs uppercase tracking-[0.18em] text-aviana-muted">Total</div>
                  <div className="font-figure text-4xl text-aviana-plum">${totalPrice}</div>
                  {tier.requires_deposit && (
                    <div className="text-xs text-aviana-muted">$100 charged today · ${totalPrice - 100} balance</div>
                  )}
                </div>
              </div>

              <button
                onClick={submit}
                disabled={submitting}
                className="btn-primary mt-8 w-full md:w-auto md:min-w-[280px]"
                data-testid="reiki-submit"
              >
                {submitting ? (
                  <><Loader2 size={16} className="inline animate-spin mr-2" />Redirecting to Stripe…</>
                ) : tier.requires_deposit ? (
                  `Reserve · Pay $100 deposit`
                ) : (
                  `Reserve · Add card on file`
                )}
              </button>
              <p className="mt-3 text-xs text-aviana-muted">
                By clicking above you agree to the cancellation policy. Not logged in? An account will be created automatically so you can view this booking later — check your email for password reset instructions.
              </p>
              <p className="mt-2 text-xs text-aviana-muted">
                Already have an account? <Link to="/login" className="text-aviana-plum hover:underline">Sign in</Link> to skip the account-creation step.
              </p>
            </div>
          </div>
        )}
      </div>
    </>
  );
}

const inputCls = "w-full bg-transparent border-b border-aviana-ink/30 focus:border-aviana-gold py-3 text-aviana-ink outline-none transition-colors text-sm";

function Input({ label, value, onChange, type = "text", testid }) {
  return (
    <div>
      <label className="block text-xs uppercase tracking-[0.18em] text-aviana-muted mb-2">{label}</label>
      <input
        type={type}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className={inputCls}
        data-testid={testid}
      />
    </div>
  );
}
